import html2canvas from 'html2canvas';

export interface PNGExportOptions {
  scale?: number;
  backgroundColor?: string;
  filename?: string;
}

/**
 * Export a DOM element to PNG image
 */
// Helper function to prepare receipt for export
function prepareReceiptForExport(clonedDoc: Document, element: HTMLElement) {
  const receiptPaper = clonedDoc.querySelector('[class*="receipt-paper"]') as HTMLElement;
  if (receiptPaper) {
    // Ensure fonts are loaded
    receiptPaper.style.fontFamily = getComputedStyle(element).fontFamily;
    // Keep overflow hidden to contain dividers, but add extra padding
    // to prevent sub-pixel clipping at the edges
    receiptPaper.style.paddingLeft = '16px';
    receiptPaper.style.paddingRight = '16px';
    
    // Preserve barcode SVG text styling - html2canvas can lose SVG text font-size
    const barcodeSvgs = receiptPaper.querySelectorAll('.barcode-container svg');
    barcodeSvgs.forEach((svg) => {
      const textElements = svg.querySelectorAll('text');
      textElements.forEach((text) => {
        // Get computed font-size and set it as inline style to preserve it
        const computedStyle = getComputedStyle(text);
        (text as SVGTextElement).style.fontSize = computedStyle.fontSize;
      });
    });

  }
}

export async function exportToPNG(
  element: HTMLElement,
  options: PNGExportOptions = {}
): Promise<Blob> {
  const { scale = 2, backgroundColor = '#ffffff' } = options;

  const canvas = await html2canvas(element, {
    scale,
    backgroundColor,
    logging: false,
    useCORS: true,
    allowTaint: true,
    removeContainer: true,
    imageTimeout: 15000,
    onclone: (clonedDoc) => prepareReceiptForExport(clonedDoc, element),
  });

  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) {
          resolve(blob);
        } else {
          reject(new Error('Failed to create PNG blob'));
        }
      },
      'image/png',
      1.0
    );
  });
}

/**
 * Export and download a DOM element as PNG
 */
export async function downloadPNG(
  element: HTMLElement,
  options: PNGExportOptions = {}
): Promise<void> {
  const { filename = 'receipt.png' } = options;

  try {
    const blob = await exportToPNG(element, options);
    const url = URL.createObjectURL(blob);

    // Try programmatic download first
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.style.display = 'none';
    document.body.appendChild(link);
    
    // Use a small delay to ensure the link is in the DOM
    await new Promise((resolve) => setTimeout(resolve, 10));
    
    link.click();
    
    // Clean up after a delay
    setTimeout(() => {
      if (document.body.contains(link)) {
        document.body.removeChild(link);
      }
      URL.revokeObjectURL(url);
    }, 100);

    // Fallback: if download doesn't work, open in new tab
    setTimeout(() => {
      // Check if download was blocked (this is a heuristic)
      if (document.body.contains(link)) {
        window.open(url, '_blank');
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }
    }, 1000);
  } catch (error) {
    console.error('Failed to download PNG:', error);
    throw error;
  }
}

/**
 * Get PNG as data URL
 */
export async function getPNGDataURL(
  element: HTMLElement,
  options: PNGExportOptions = {}
): Promise<string> {
  const { scale = 2, backgroundColor = '#ffffff' } = options;

  const canvas = await html2canvas(element, {
    scale,
    backgroundColor,
    logging: false,
    useCORS: true,
    allowTaint: true,
    removeContainer: true,
    imageTimeout: 15000,
    onclone: (clonedDoc) => prepareReceiptForExport(clonedDoc, element),
  });

  return canvas.toDataURL('image/png', 1.0);
}

